<template>
  <div ref="stage" class="reward-hero-burst" aria-live="polite" aria-atomic="true">
    <div class="reward-hero-burst__fx" aria-hidden="true">
      <div
        v-for="index in ringCount"
        :key="`ring-${index}`"
        class="reward-hero-burst__ring"
      />
      <img
        v-for="index in particleCount"
        :key="`particle-${index}`"
        :src="resolvedCoin"
        alt=""
        class="reward-hero-burst__particle"
      />
    </div>

    <div ref="copy" class="reward-hero-burst__copy">
      <div class="reward-hero-burst__eyebrow">{{ eyebrow }}</div>
      <div class="reward-hero-burst__value-row">
        <div class="flex justify-between gap-4 w-[224px]">
          <img ref="coin" :src="resolvedCoin" alt="" class="reward-hero-burst__coin" />
          <div ref="pointsEl" class="reward-hero-burst__points w-full">+0</div>
        </div>
      </div>
      <div ref="subtitleEl" class="reward-hero-burst__subtitle">{{ subtitle }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import gsap from 'gsap'

import defaultCoin from '@/../public/img/game/bolt.svg'

const props = withDefaults(defineProps<{
  points: number
  coinSrc?: string
  eyebrow?: string
  subtitle?: string
  particleCount?: number
  ringCount?: number
  trigger?: string | number
  autoplay?: boolean
  fadeoutDelay?: number
}>(), {
  coinSrc: '',
  eyebrow: 'Recompensa recebida',
  subtitle: 'Excelente! Continue assim.',
  particleCount: 14,
  ringCount: 2,
  trigger: 0,
  autoplay: false,
  fadeoutDelay: 700,
})

const emit = defineEmits<{
  start: []
  complete: []
  fadeoutComplete: []
}>()

const stage = ref<HTMLElement | null>(null)
const copy = ref<HTMLElement | null>(null)
const coin = ref<HTMLImageElement | null>(null)
const pointsEl = ref<HTMLElement | null>(null)
const subtitleEl = ref<HTMLElement | null>(null)
const resolvedCoin = computed(() => props.coinSrc || defaultCoin)
let timeline: gsap.core.Timeline | null = null
let fadeout: gsap.core.Tween | null = null

function formatPoints(value: number) {
  return `+${Math.round(value).toLocaleString('pt-BR')}`
}

async function play() {
  await nextTick()
  if (!stage.value || !copy.value || !coin.value || !pointsEl.value || !subtitleEl.value) return

  timeline?.kill()
  fadeout?.kill()
  fadeout = null
  gsap.killTweensOf(stage.value.querySelectorAll('*'))

  const particles = Array.from(stage.value.querySelectorAll<HTMLElement>('.reward-hero-burst__particle'))
  const rings = Array.from(stage.value.querySelectorAll<HTMLElement>('.reward-hero-burst__ring'))
  const counter = { value: 0 }

  emit('start')

  if (stage.value) {
    gsap.set(stage.value, { opacity: 1 })
  }
  gsap.set(copy.value, { opacity: 1 })
  gsap.set(coin.value, { scale: 0, rotation: -90, transformOrigin: '50% 50%' })
  gsap.set(pointsEl.value, { scale: 0.7, opacity: 0, transformOrigin: '50% 50%' })
  gsap.set(subtitleEl.value, { y: 12, opacity: 0 })
  pointsEl.value.textContent = '+0'

  particles.forEach((particle, index) => {
    const angle = ((Math.PI * 2) / Math.max(1, particles.length)) * index + gsap.utils.random(-0.1, 0.1)
    const distance = gsap.utils.random(74, 142)
    gsap.set(particle, {
      left: '50%',
      top: '50%',
      xPercent: -50,
      yPercent: -50,
      x: 0,
      y: 0,
      rotation: gsap.utils.random(-45, 45),
      scale: 0.15,
      opacity: 0,
      transformOrigin: '50% 50%',
    })
    particle.dataset.targetX = String(Math.cos(angle) * distance)
    particle.dataset.targetY = String(Math.sin(angle) * distance)
  })

  rings.forEach((ring) => {
    gsap.set(ring, {
      left: '50%',
      top: '50%',
      xPercent: -50,
      yPercent: -50,
      width: 32,
      height: 32,
      opacity: 0,
    })
  })

  timeline = gsap.timeline({
    onComplete: () => {
      emit('complete')
      if (props.fadeoutDelay > 0 && stage.value) {
        gsap.delayedCall(props.fadeoutDelay / 1000, () => {
          if (stage.value) {
            fadeout = gsap.to(stage.value, {
              opacity: 0,
              duration: 0.4,
              ease: 'power2.out',
              onComplete: () => emit('fadeoutComplete'),
            })
          } else {
            emit('fadeoutComplete')
          }
        })
      }
    },
  })

  timeline
    .to(coin.value, { scale: 1.16, rotation: 10, duration: 0.45, ease: 'back.out(2.4)' }, 0)
    .to(coin.value, { scale: 1, rotation: 0, duration: 0.2, ease: 'power2.out' }, 0.28)
    .to(pointsEl.value, { scale: 1, opacity: 1, duration: 0.35, ease: 'back.out(2)' }, 0.18)
    .to(counter, {
      value: props.points,
      duration: 0.65,
      ease: 'power2.out',
      onUpdate: () => {
        if (pointsEl.value) pointsEl.value.textContent = formatPoints(counter.value)
      },
    }, 0.18)
    .to(subtitleEl.value, { y: 0, opacity: 1, duration: 0.3, ease: 'power2.out' }, 0.42)

  particles.forEach((particle, index) => {
    const x = Number(particle.dataset.targetX ?? 0)
    const y = Number(particle.dataset.targetY ?? 0)
    const delay = 0.11 + index * 0.007
    timeline!
      .to(particle, {
        x,
        y,
        rotation: gsap.utils.random(-260, 260),
        scale: gsap.utils.random(0.45, 0.92),
        opacity: 1,
        duration: 0.48,
        ease: 'power3.out',
      }, delay)
      .to(particle, {
        x: x * 1.08,
        y: y * 1.08 + 10,
        opacity: 0,
        scale: 0.12,
        duration: 0.42,
        ease: 'power2.in',
      }, delay + 0.48)
  })

  rings.forEach((ring, index) => {
    timeline!
      .to(ring, { opacity: 0.62, duration: 0.08 }, 0.1 + index * 0.07)
      .to(ring, {
        width: 230 + index * 72,
        height: 230 + index * 72,
        opacity: 0,
        duration: 0.78,
        ease: 'power2.out',
      }, 0.16 + index * 0.07)
  })
}

defineExpose({ play })
watch(() => props.trigger, (value, oldValue) => { if (value !== oldValue) void play() })
onMounted(() => { if (props.autoplay) void play() })
onBeforeUnmount(() => {
  timeline?.kill()
  fadeout?.kill()
  fadeout = null
  if (stage.value) gsap.killTweensOf(stage.value.querySelectorAll('*'))
})
</script>

<style scoped>
.reward-hero-burst {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: grid;
  place-items: center;
  overflow: hidden;
  isolation: isolate;
  contain: layout paint;
  pointer-events: none;
}
.reward-hero-burst__fx {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
}
.reward-hero-burst__copy {
  position: relative;
  z-index: 2;
  width: min(92%, 32rem);
  text-align: center;
  opacity: 0;
}
.reward-hero-burst__eyebrow {
  font-size: .75rem;
  line-height: 1rem;
  text-transform: uppercase;
  letter-spacing: .22em;
  font-weight: 900;
  color: #8b5cf6;
}
.reward-hero-burst__value-row {
  margin-top: .5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .75rem;
}
.reward-hero-burst__coin {
  display: block;
  width: 4rem;
  height: 4rem;
  flex: 0 0 auto;
  will-change: transform;
}
.reward-hero-burst__points {
  display: block;
  font-size: clamp(2.75rem, 8vw, 3.75rem);
  line-height: 1;
  font-weight: 900;
  letter-spacing: -.05em;
  color: #b198df;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  will-change: transform, opacity;
}
.reward-hero-burst__subtitle {
  margin-top: .5rem;
  font-size: .875rem;
  line-height: 1.25rem;
  font-weight: 600;
  color: #64748b;
  will-change: transform, opacity;
}
.reward-hero-burst__particle {
  position: absolute;
  display: block;
  width: 1.5rem;
  height: 1.5rem;
  margin: 0;
  max-width: none;
  pointer-events: none;
  user-select: none;
  opacity: 0;
  will-change: transform, opacity;
}
.reward-hero-burst__ring {
  position: absolute;
  width: 2rem;
  height: 2rem;
  border: 2px solid rgba(174, 84, 177, .42);
  border-radius: 9999px;
  pointer-events: none;
  opacity: 0;
  will-change: width, height, opacity;
}
@media (prefers-reduced-motion: reduce) {
  .reward-hero-burst__particle,
  .reward-hero-burst__ring { display: none; }
}
</style>
